import { useEffect, useRef, useState } from 'react';
import { useQueryClient, type Query, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { post, type ApiData } from '@app/api/typed';
import type { ReadingScope } from './reading';

export type ReadingStarted = ApiData<'/api/v1/runtime/readings', 'post'>;

export interface ClusterReadingOptions {
  scope: ReadingScope;
  /** Obrigatório no escopo `identity`: sem ele, nada é disparado. */
  identityId?: string | null;
  /** `false` adia o disparo (a gaveta sem identidade, a tela sem permissão). Padrão: `true`. */
  enabled?: boolean;
  /**
   * As consultas que mostram a foto deste escopo. Quando o `POST` volta, elas são relidas: a resposta nova traz
   * `in_flight`, e o `readingInterval` de cada uma passa a reler sozinho até a leitura terminar.
   */
  refresh: readonly QueryKey[];
}

/** A consulta em erro fica como está: a tela mostra o erro e o «Tentar de novo», e quem relê é a pessoa. */
function notErrored(query: Query): boolean {
  return query.state.status !== 'error';
}

/**
 * Relê as consultas que mostram a foto, depois que o `POST` voltou, sem CANCELAR nenhuma. Cancelar a primeira leitura
 * da tela, ainda em curso, faria a tela piscar: sem dado, o cancelamento passa pelo estado de erro e volta. Quem já
 * estava lendo quando o `POST` voltou pode ter saído antes da leitura nova começar; por isso, depois dela, mais uma.
 */
function refreshPhoto(queryClient: QueryClient, queryKey: QueryKey): void {
  const filters = { queryKey, type: 'active' as const, predicate: notErrored };
  const busy = queryClient.isFetching({ queryKey }) > 0;
  void queryClient
    .refetchQueries(filters, { cancelRefetch: false })
    .then(() => (busy ? queryClient.refetchQueries(filters, { cancelRefetch: false }) : undefined));
}

export interface ClusterReading {
  /** O `POST` saiu e não voltou: a tela já diz «lendo», embora a foto ainda não diga (`readingView`, `pending`). */
  pending: boolean;
  /** A resposta do `POST`: a leitura aberta agora, ou a que já estava em curso. Nula até ele voltar, ou se falhou. */
  started: ReadingStarted | null;
  /** O `POST` voltou, com resposta ou com falha. Antes do primeiro disparo, `false`. */
  done: boolean;
}

/**
 * Dispara a leitura nova de um escopo ao abrir a tela (aim-6ph.13, decisão 2): `cluster` em T3 e T4, `identity` na
 * gaveta da identidade, `pdp` em Security Contracts. UMA vez por abertura: a tela não relê o executor, relê a foto.
 *
 * O servidor é idempotente (decisão 3): com uma leitura do mesmo escopo em curso, ou com a última tentativa nos
 * últimos 15 s, ele não abre outra e devolve a que existe. Por isso duas telas abertas juntas não leem o executor
 * duas vezes, e a tela não precisa de guarda própria contra isso.
 *
 * A falha do disparo não vira erro da tela: a foto continua na tela, e o bloco `reading` diz o que sabe. É o mesmo
 * critério da fila de T9, que não depende do cluster para ser útil.
 */
export function useClusterReading(options: ClusterReadingOptions): ClusterReading {
  const { scope, identityId, enabled = true, refresh } = options;
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [started, setStarted] = useState<ReadingStarted | null>(null);
  const [done, setDone] = useState(false);
  // O que já foi disparado nesta montagem. Uma referência, e não um estado: no `StrictMode` o efeito corre duas
  // vezes na mesma montagem, e a segunda não pode abrir outra leitura.
  const fired = useRef<string | null>(null);
  // As chaves mudam de identidade a cada render (são literais de quem chama); a última vale no fim do `POST`.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const key = `${scope}:${identityId ?? ''}`;
  const ready = enabled && (scope !== 'identity' || Boolean(identityId));

  useEffect(() => {
    if (!ready || fired.current === key) return;
    fired.current = key;
    setPending(true);
    setStarted(null);
    setDone(false);
    const body =
      scope === 'identity' && identityId ? { scope, identity_id: identityId } : { scope };
    post('/api/v1/runtime/readings', { body })
      .then((data) => setStarted(data))
      .catch(() => setStarted(null))
      .finally(() => {
        // Sem guarda de «ainda montado»: no `StrictMode` a limpeza do primeiro efeito correria antes da resposta, e a
        // tela ficaria «lendo» para sempre. O React ignora o estado de um componente que já saiu.
        setPending(false);
        setDone(true);
        for (const queryKey of refreshRef.current) refreshPhoto(queryClient, queryKey);
      });
  }, [ready, key, scope, identityId, queryClient]);

  return { pending, started, done };
}
